import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  FuelCellRow,
  FuelCellSample,
  FuelCellSummaryStats,
  LOW_FUEL_THRESHOLD_PERCENT,
} from "../models/FuelCellRow";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { DIAGNOSTIC_FUEL_LEVEL_ID, FUEL_CELL_GROUP_NAME } from "@/constants/constants";
import { useGroupByName } from "@/hooks/groups/useGroupByName";
import { useStatusDataByDeviceAndDate } from "@/hooks/statusData/useStatusDataByDeviceAndDate";
import { useStatusInfosByIds } from "@/hooks/statusInfos/useStatusInfosByIds";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";

const DIAGNOSTIC_ODOMETER_ID = "DiagnosticOdometerId";
const HISTORY_RESULTS_LIMIT = 50000;
const REFILL_THRESHOLD_PERCENT = 10;
const NOISE_THRESHOLD_PERCENT = 0.5;

type FuelTankCapacityValue =
  | number
  | { volume?: number | null; source?: unknown }
  | null
  | undefined;

type DeviceWithFuelTank = DeviceWithVehicleInfo & {
  fuelTankCapacity?: FuelTankCapacityValue;
};

interface DeviceHistory {
  deviceId: string;
  samples: FuelCellSample[];
  truncated: boolean;
}

function getTankCapacityKg(device: DeviceWithFuelTank): number | null {
  const capacity = device.fuelTankCapacity;
  if (typeof capacity === "number" && Number.isFinite(capacity)) {
    return capacity;
  }
  if (
    capacity &&
    typeof capacity === "object" &&
    typeof capacity.volume === "number" &&
    Number.isFinite(capacity.volume)
  ) {
    return capacity.volume;
  }
  return null;
}

function sortSamples(samples: FuelCellSample[]): FuelCellSample[] {
  return [...samples].sort(
    (a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime(),
  );
}

function summarizeHistory(sorted: FuelCellSample[]): {
  usedPercent: number;
  refillCount: number;
} | null {
  if (sorted.length < 2) {
    return null;
  }
  let usedPercent = 0;
  let refillCount = 0;
  for (let i = 1; i < sorted.length; i++) {
    const delta = sorted[i].data - sorted[i - 1].data;
    if (delta >= REFILL_THRESHOLD_PERCENT) {
      refillCount++;
    } else if (delta < -NOISE_THRESHOLD_PERCENT) {
      usedPercent += -delta;
    }
  }
  return { usedPercent, refillCount };
}

export function useFuelCellData(fromDate: Date, toDate: Date) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const {
    data: group,
    isLoading: isLoadingGroup,
    error: groupError,
  } = useGroupByName(FUEL_CELL_GROUP_NAME);

  const groupId = group?.id ?? null;

  const {
    data: devices = [],
    isLoading: isLoadingDevices,
    isFetching: isFetchingDevices,
    error: devicesError,
  } = useQuery<DeviceWithFuelTank[]>({
    queryKey: ["fuelCellDevices", activeSessionId, groupId],
    enabled: !!groupId,
    queryFn: async ({ signal }) => {
      if (!groupId) return [];
      const result = await geotabClient.call(
        "Get",
        {
          typeName: "Device",
          search: {
            fromDate: new Date(),
            groupFilterCondition: { groupId },
          },
          propertySelector: {
            fields: [
              "id",
              "name",
              "licenseplate",
              "serialNumber",
              "vehicleIdentificationNumber",
              "fuelTankCapacity",
              "groups",
            ],
            isIncluded: true,
          },
          resultsLimit: 1000,
        },
        signal,
      );
      return (result as DeviceWithFuelTank[] | null) ?? [];
    },
    refetchInterval: 60000,
    placeholderData: keepPreviousData,
  });

  const deviceIds = useMemo(() => devices.map(d => d.id), [devices]);

  const {
    data: statusInfos = {},
    isLoading: isLoadingStatus,
    isFetching: isFetchingStatus,
    error: statusError,
  } = useStatusInfosByIds(deviceIds, [DIAGNOSTIC_FUEL_LEVEL_ID]);

  const fromIso = useMemo(() => fromDate.toISOString(), [fromDate]);
  const toIso = useMemo(() => toDate.toISOString(), [toDate]);

  const {
    data: histories = [],
    isLoading: isLoadingHistory,
    isFetching: isFetchingHistory,
    error: historyError,
  } = useQuery<DeviceHistory[]>({
    queryKey: ["fuelCellHistory", activeSessionId, deviceIds, fromIso, toIso],
    enabled: deviceIds.length > 0,
    queryFn: async ({ signal }) => {
      const results = await Promise.all(
        deviceIds.map(async deviceId => {
          const samples = (await geotabClient.get(
            "StatusData",
            {
              fromDate,
              toDate,
              diagnosticSearch: { id: DIAGNOSTIC_FUEL_LEVEL_ID },
              deviceSearch: { id: deviceId },
            },
            HISTORY_RESULTS_LIMIT,
            signal,
          )) as FuelCellSample[];
          return {
            deviceId,
            samples: samples ?? [],
            truncated: (samples?.length ?? 0) >= HISTORY_RESULTS_LIMIT,
          };
        }),
      );
      return results;
    },
    placeholderData: keepPreviousData,
  });

  const historyByDevice = useMemo(() => {
    const map = new Map<string, { samples: FuelCellSample[]; truncated: boolean }>();
    for (const h of histories) {
      map.set(h.deviceId, { samples: sortSamples(h.samples), truncated: h.truncated });
    }
    return map;
  }, [histories]);

  const samplesByDevice = useMemo(() => {
    const map = new Map<string, FuelCellSample[]>();
    for (const [id, h] of historyByDevice) map.set(id, h.samples);
    return map;
  }, [historyByDevice]);

  const odometerStartArgs = useMemo(
    () => devices.map(d => ({ deviceId: d.id, dateTime: fromDate })),
    [devices, fromDate],
  );
  const odometerEndArgs = useMemo(
    () => devices.map(d => ({ deviceId: d.id, dateTime: toDate })),
    [devices, toDate],
  );
  const {
    data: odometerStart = {},
    isLoading: isLoadingOdometerStart,
    isFetching: isFetchingOdometerStart,
    error: odometerStartError,
  } = useStatusDataByDeviceAndDate(odometerStartArgs, DIAGNOSTIC_ODOMETER_ID);
  const {
    data: odometerEnd = {},
    isLoading: isLoadingOdometerEnd,
    isFetching: isFetchingOdometerEnd,
    error: odometerEndError,
  } = useStatusDataByDeviceAndDate(odometerEndArgs, DIAGNOSTIC_ODOMETER_ID);

  const rows: FuelCellRow[] = useMemo(
    () =>
      devices.map(device => {
        const tankCapacityKg = getTankCapacityKg(device);

        const statusInfo = statusInfos[device.id];
        const fuelEntry = statusInfo?.statusData?.find(
          sd => sd.diagnostic?.id === DIAGNOSTIC_FUEL_LEVEL_ID,
        );
        const rawLevel = fuelEntry?.data;
        const levelPercent =
          typeof rawLevel === "number" && Number.isFinite(rawLevel) ? rawLevel : null;
        const updatedAt = fuelEntry?.dateTime ? new Date(fuelEntry.dateTime) : null;

        const remainingKg =
          levelPercent != null && tankCapacityKg != null
            ? (levelPercent / 100) * tankCapacityKg
            : null;
        const usedKg =
          levelPercent != null && tankCapacityKg != null
            ? ((100 - levelPercent) / 100) * tankCapacityKg
            : null;

        const history = historyByDevice.get(device.id);
        const summary = history ? summarizeHistory(history.samples) : null;
        const usedRangeKg =
          summary && tankCapacityKg != null ? (summary.usedPercent / 100) * tankCapacityKg : null;
        const refillCount = summary ? summary.refillCount : null;

        const startOdo = odometerStart[device.id]?.data;
        const endOdo = odometerEnd[device.id]?.data;
        const distanceKm =
          typeof startOdo === "number" &&
          typeof endOdo === "number" &&
          Number.isFinite(startOdo) &&
          Number.isFinite(endOdo) &&
          endOdo >= startOdo
            ? (endOdo - startOdo) / 1000
            : null;
        const avgConsumptionKgPer100Km =
          distanceKm != null && distanceKm > 0 && usedRangeKg != null
            ? (usedRangeKg / distanceKm) * 100
            : null;

        return {
          id: device.id,
          name: device.name ?? "",
          licensePlate: device.licensePlate ?? "",
          vin: device.vehicleIdentificationNumber ?? "",
          serialNumber: device.serialNumber ?? "",
          tankCapacityKg,
          levelPercent,
          remainingKg,
          usedKg,
          updatedAt,
          usedRangeKg,
          refillCount,
          distanceKm,
          avgConsumptionKgPer100Km,
          rangeTruncated: history?.truncated ?? false,
        };
      }),
    [devices, statusInfos, historyByDevice, odometerStart, odometerEnd],
  );

  const truncatedDeviceNames = useMemo(
    () => rows.filter(r => r.rangeTruncated).map(r => r.name || r.licensePlate || r.id),
    [rows],
  );

  const summaryStats: FuelCellSummaryStats = useMemo(() => {
    const totalUsedKg = rows.reduce((sum, r) => sum + (r.usedRangeKg ?? 0), 0);
    const totalRefills = rows.reduce((sum, r) => sum + (r.refillCount ?? 0), 0);
    const levels = rows.map(r => r.levelPercent).filter((v): v is number => v != null);
    const avgLevelPercent =
      levels.length > 0 ? levels.reduce((s, v) => s + v, 0) / levels.length : null;
    const lowFuelCount = rows.filter(
      r => r.levelPercent != null && r.levelPercent < LOW_FUEL_THRESHOLD_PERCENT,
    ).length;
    return {
      totalUsedKg,
      totalRefills,
      avgLevelPercent,
      lowFuelCount,
      deviceCount: rows.length,
    };
  }, [rows]);

  const groupMissing = !isLoadingGroup && !groupError && group === null;

  return {
    rows,
    samplesByDevice,
    isLoading:
      isLoadingGroup ||
      isLoadingDevices ||
      (deviceIds.length > 0 &&
        (isLoadingStatus || isLoadingHistory || isLoadingOdometerStart || isLoadingOdometerEnd)),
    isFetching:
      isFetchingDevices ||
      isFetchingStatus ||
      isFetchingHistory ||
      isFetchingOdometerStart ||
      isFetchingOdometerEnd,
    error:
      (groupError as Error | null) ??
      (devicesError as Error | null) ??
      (statusError as Error | null) ??
      (historyError as Error | null) ??
      (odometerStartError as Error | null) ??
      (odometerEndError as Error | null) ??
      null,
    groupMissing,
    truncatedDeviceNames,
    summaryStats,
  };
}
